import { createContext, useCallback, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';

import type { GetPropertyTypesQuery } from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useLocale } from '@shared/hooks/locale';

type PropertyType = GetPropertyTypesQuery['property_types'][number];

type PropertyTypesContextType = {
  getPropertyTypeLabel: (name: string | null) => string;
};

const PropertyTypesContext = createContext<PropertyTypesContextType | null>(
  null,
);

type PropertyTypesProviderProps = {
  children: React.ReactNode;
};

// Helper to format property type for display when no label is available
const formatPropertyType = (type: string | null | undefined): string => {
  if (!type) {
    return '';
  }

  // Remove common prefixes and format nicely
  return type
    .replace(/_/g, ' ')
    .replace(/^(house|apartment|park|indus|prop|gastro|agri)\s/i, '')
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
};

export const PropertyTypesProvider = ({
  children,
}: PropertyTypesProviderProps) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(GET_PROPERTY_TYPES);

  const propertyTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    data?.property_types?.forEach((pt: PropertyType) => {
      if (pt.name && pt.label) {
        map.set(pt.name, pt.label);
      }
    });
    return map;
  }, [data]);

  const getPropertyTypeLabel = useCallback(
    (type: string | null) => {
      if (!type) {
        return formatPropertyType(type);
      }

      // First try to find the label from CRM property types
      const crmLabel = propertyTypeMap.get(type);

      if (crmLabel) {
        return crmLabel;
      }

      // Fallback to formatted string for scrapers data
      return formatPropertyType(type);
    },
    [propertyTypeMap],
  );

  const contextValue = useMemo(
    () => ({ getPropertyTypeLabel }),
    [getPropertyTypeLabel],
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Alert severity="error">{t('Failed to load property types')}</Alert>;
  }

  return (
    <PropertyTypesContext.Provider value={contextValue}>
      {children}
    </PropertyTypesContext.Provider>
  );
};

export const usePropertyTypes = () => {
  const context = useContext(PropertyTypesContext);
  if (!context) {
    throw new Error(
      'Property types context not found - usePropertyTypes must be used within PropertyTypesProvider',
    );
  }
  return context;
};
